import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Space, Card, Row, Col, Tooltip, message } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const KeyValueEditor = ({ value, onChange, placeholder, disabled, onKeyPicked }) => {
  const [keyValuePairs, setKeyValuePairs] = useState([]);
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState('');
  const [isInternalChange, setIsInternalChange] = useState(false);

  const copyToClipboard = useCallback(async (text) => {
    if (!text) return false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      // fall through to execCommand
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch (e) {
      return false;
    }
  }, []);

  // Parse the initial value when component mounts or value changes from external source
  useEffect(() => {
    // Skip if this is an internal change (from user typing)
    if (isInternalChange) {
      setIsInternalChange(false);
      return;
    }

    if (value) {
      try {
        parseTextToKeyValuePairs(value);
      } catch (error) {
        console.warn('Failed to parse initial value, using raw mode:', error);
        setRawText(value);
        setRawMode(true);
      }
    } else {
      setKeyValuePairs([{ key: '', value: '', id: 1 }]);
      setRawText('');
    }
  }, [value, isInternalChange]);

  // Parse text format to key-value pairs
  const parseTextToKeyValuePairs = (text) => {
    if (!text || text.trim() === '') {
      setKeyValuePairs([{ key: '', value: '', id: 1 }]);
      return;
    }

    const lines = text.split('\n').filter(line => line.trim() !== '');
    const pairs = [];
    let idCounter = 1;

    for (const line of lines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) {
        // If no colon found, treat entire line as key with empty value
        pairs.push({
          key: line.trim(),
          value: '',
          id: idCounter++
        });
      } else {
        const key = line.substring(0, colonIndex).trim();
        const value = line.substring(colonIndex + 1).trim();
        pairs.push({
          key,
          value,
          id: idCounter++
        });
      }
    }

    if (pairs.length === 0) {
      pairs.push({ key: '', value: '', id: 1 });
    }

    setKeyValuePairs(pairs);
    setRawMode(false);
  };

  // Convert key-value pairs to text format
  const keyValuePairsToText = (pairs) => {
    return pairs
      .filter(pair => pair.key.trim() !== '' || pair.value.trim() !== '')
      .map(pair => `${pair.key}: ${pair.value}`)
      .join('\n');
  };

  // Handle key-value pair changes
  const handlePairChange = (id, field, newValue) => {
    const updatedPairs = keyValuePairs.map(pair =>
      pair.id === id ? { ...pair, [field]: newValue } : pair
    );
    setKeyValuePairs(updatedPairs);
    
    const textValue = keyValuePairsToText(updatedPairs);
    setIsInternalChange(true); // Mark as internal change to prevent useEffect re-parsing
    if (onChange) {
      onChange(textValue);
    }
  };

  // Add new key-value pair
  const addPair = () => {
    const maxId = Math.max(...keyValuePairs.map(p => p.id), 0);
    const newPair = { key: '', value: '', id: maxId + 1 };
    const updatedPairs = [...keyValuePairs, newPair];
    setKeyValuePairs(updatedPairs);
  };

  // Remove key-value pair
  const removePair = (id) => {
    if (keyValuePairs.length <= 1) {
      message.warning('At least one key-value pair is required');
      return;
    }
    
    const updatedPairs = keyValuePairs.filter(pair => pair.id !== id);
    setKeyValuePairs(updatedPairs);
    
    const textValue = keyValuePairsToText(updatedPairs);
    setIsInternalChange(true); // Mark as internal change
    if (onChange) {
      onChange(textValue);
    }
  };

  // Handle raw text changes
  const handleRawTextChange = (e) => {
    const newValue = e.target.value;
    setRawText(newValue);
    setIsInternalChange(true); // Mark as internal change
    if (onChange) {
      onChange(newValue);
    }
  };

  // Switch to raw mode
  const switchToRawMode = () => {
    const textValue = keyValuePairsToText(keyValuePairs);
    setRawText(textValue);
    setRawMode(true);
  };

  // Switch to key-value mode
  const switchToKeyValueMode = () => {
    try {
      parseTextToKeyValuePairs(rawText);
      setRawMode(false);
    } catch (error) {
      message.error('Invalid format. Please check your key-value pairs.');
    }
  };

  // Validate JSON value
  const validateJsonValue = (value) => {
    if (!value || value.trim() === '') return true;
    
    // If it looks like JSON (starts with { or [), try to parse it
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        JSON.parse(value);
        return true;
      } catch (error) {
        return false;
      }
    }
    return true;
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '12px', color: '#666' }}>
            Context Data Key-Value Pairs
          </span>
          <Tooltip title="Add key-value pairs for workflow context. Values can be plain text or JSON objects.">
            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
          </Tooltip>
        </div>
        <Space>
          <Button
            type="text"
            size="small"
            onClick={rawMode ? switchToKeyValueMode : switchToRawMode}
          >
            {rawMode ? 'Switch to Key-Value' : 'Switch to Raw'}
          </Button>
        </Space>
      </div>

      {rawMode ? (
        <TextArea
          value={rawText}
          onChange={handleRawTextChange}
          placeholder={placeholder || "key1: value1\nkey2: value2\nkey3: {\"nested\": \"object\"}"}
          rows={6}
          disabled={disabled}
        />
      ) : (
        <Card size="small" style={{ background: '#fafafa' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {keyValuePairs.map((pair, index) => (
              <Row key={pair.id} gutter={8} align="middle">
                <Col span={8}>
                  <Input
                    placeholder="Key"
                    value={pair.key}
                    onChange={(e) => handlePairChange(pair.id, 'key', e.target.value)}
                    disabled={disabled}
                    size="small"
                  />
                </Col>
                <Col span={1}>
                  <Tooltip title={pair.key?.trim() ? `Copy {context.${pair.key.trim()}}` : 'Enter a key to copy'}>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      disabled={disabled || !pair.key?.trim()}
                      onClick={async () => {
                        const trimmedKey = (pair.key || '').trim();
                        if (!trimmedKey) {
                          message.warning('No key to copy');
                          return;
                        }
                        const token = `{context.${trimmedKey}}`;
                        const ok = await copyToClipboard(token);
                        if (ok) {
                          message.success(`Copied ${token}`);
                        } else {
                          message.error('Failed to copy to clipboard');
                        }
                        if (onKeyPicked) {
                          onKeyPicked(token, { key: trimmedKey, value: pair.value });
                        }
                      }}
                    />
                  </Tooltip>
                </Col>
                <Col span={13}>
                  <Input
                    placeholder="Value (text or JSON)"
                    value={pair.value}
                    onChange={(e) => handlePairChange(pair.id, 'value', e.target.value)}
                    disabled={disabled}
                    size="small"
                    status={!validateJsonValue(pair.value) ? 'error' : ''}
                  />
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => removePair(pair.id)}
                    disabled={disabled || keyValuePairs.length <= 1}
                  />
                </Col>
              </Row>
            ))}
            
            <Button
              type="dashed"
              onClick={addPair}
              icon={<PlusOutlined />}
              size="small"
              style={{ width: '100%' }}
              disabled={disabled}
            >
              Add Key-Value Pair
            </Button>
          </Space>
        </Card>
      )}
      
      {!rawMode && (
        <div style={{ marginTop: 8, fontSize: '11px', color: '#999' }}>
          Tip: For JSON objects, use valid JSON syntax like {`{"key": "value"}`}
        </div>
      )}
    </div>
  );
};

export default KeyValueEditor;
